/**
 * Anthropic model + API-key configuration for the web app.
 *
 * Resolution order (highest priority first):
 *   1. Runtime override — `localStorage['4cm_anthropic_model']`
 *   2. Build-time env var — `VITE_ANTHROPIC_MODEL`
 *   3. Compile-time default — `DEFAULT_MODEL` below
 *
 * Callers use `getModel()` at call-time (not import-time) so runtime overrides
 * take effect without a page reload. `setModel()` persists the override and
 * broadcasts a change event so any settings UI can stay in sync.
 *
 * The API key never lives in the browser bundle — all requests go through
 * the anthropic-proxy / vision-proxy edge functions (see ai.ts).
 */

/** Pinned by default. Bump this constant when we validate a new snapshot.
 *  Every surface without a per-surface default in aiSurfaces.ts and
 *  without an override inherits this. */
export const DEFAULT_MODEL = 'claude-sonnet-4-6';

const STORAGE_KEY = '4cm_anthropic_model';
const CHANGE_EVENT = '4cm_anthropic_model_change';

/** Current effective model — prefer runtime override, then env var, then default. */
export function getModel(): string {
  try {
    const stored = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
    if (stored && stored.trim()) return stored.trim();
  } catch { /* localStorage unavailable (SSR) */ }
  const envModel = (import.meta as any).env?.VITE_ANTHROPIC_MODEL;
  if (typeof envModel === 'string' && envModel.trim()) return envModel.trim();
  return DEFAULT_MODEL;
}

/** Persist a runtime model override. Pass `null` to clear it. */
export function setModel(model: string | null): void {
  try {
    if (model === null || model === '') {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, model);
    }
    window.dispatchEvent(new Event(CHANGE_EVENT));
  } catch { /* ignore */ }
}

/** Subscribe to model-preference changes (returns unsubscribe). */
export function onModelChange(cb: () => void): () => void {
  const handler = () => cb();
  window.addEventListener(CHANGE_EVENT, handler);
  window.addEventListener('storage', handler);
  return () => {
    window.removeEventListener(CHANGE_EVENT, handler);
    window.removeEventListener('storage', handler);
  };
}
